"use client";

import { Badge, Text } from "@components/theming";
import { useIsShopsite } from "@context/DomainContext";
import { CalendarIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { MegaphoneIcon } from "@heroicons/react/24/solid";
import { getDayAndMonth } from "@utils/getDayAndMonth";
import { joinClassNames } from "@utils/helpers";
import { useResizeObserver } from "@utils/hooks";
import { motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";

interface Props {
  landingPage: {
    id: number;
    name: string;
    slug: string;
    custom: {
      header: string;
      footer: string;
    };
    image: string;
    order: number;
    date?: {
      start: string | null;
      end: string | null;
      displayDate?: string | null;
    };
    region?: string;
  };
}

export default function LandingPageLink({ landingPage }: Props) {
  const [hoveringDate, setHoveringDate] = useState(false);
  const [dateRangeVisible, setDateRangeVisible] = useState(false);
  const isShopsite = useIsShopsite();
  const t = useTranslations("Utils");
  const { ref, clientHeight } = useResizeObserver();

  const dateRangeText = landingPage?.date?.displayDate
    ? landingPage.date.displayDate
    : landingPage?.date?.start
    ? `${getDayAndMonth(
        landingPage.date.start,
        landingPage.date.end &&
          landingPage.date.start.split("T")[0] !==
            landingPage.date.end.split("T")[0]
          ? {
              day: "numeric",
              month: "short",
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
          : {
              day: "numeric",
              month: "short",
              year: "numeric",
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }
      )}
  ${
    landingPage.date.end &&
    landingPage.date.start.split("T")[0] !== landingPage.date.end.split("T")[0]
      ? ` ${t("to")} ${getDayAndMonth(landingPage.date.end, {
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })}`
      : ""
  }`
    : null;

  return (
    <Link
      href={landingPage.slug || "#"}
      onMouseEnter={(e) => {
        e.preventDefault();
        setHoveringDate(true);
        setDateRangeVisible(true);
      }}
      onMouseLeave={(e) => {
        e.preventDefault();
        setHoveringDate(false);
        setTimeout(() => setDateRangeVisible(false), 200);
      }}
      className={joinClassNames(
        "group flex flex-col gap-1 p-2 min-w-[12.5rem] h-fit grow transition-colors ease-in-out duration-500",
        isShopsite
          ? "sm:hover:bg-indigo-50/75 rounded-2xl"
          : "sm:hover:bg-[color:var(--accent-a2)] rounded-[var(--radius-4)]"
      )}
    >
      <div className="relative">
        {landingPage.image.trim() ? (
          <Image
            src={landingPage.image.trim()}
            alt={landingPage.name}
            width={512}
            height={256}
            className={joinClassNames(
              "object-cover w-full aspect-[2/1]",
              isShopsite ? "rounded-xl" : "rounded-[var(--radius-3)]"
            )}
            quality={100}
          />
        ) : (
          <div
            className={joinClassNames(
              "flex items-center justify-center w-full aspect-[2/1]",
              isShopsite
                ? "bg-indigo-50 rounded-xl"
                : "bg-[color:var(--accent-a2)] rounded-[var(--radius-3)]"
            )}
          >
            <MegaphoneIcon
              className={joinClassNames(
                "w-1/2 h-1/2",
                isShopsite ? "text-indigo-800" : "text-[color:var(--accent-9)]"
              )}
            />
          </div>
        )}
        {dateRangeText ? (
          <>
            {/* invisible span to measure height of the text */}
            <span
              className="invisible text-sm max-w-[calc(100%-44px)] absolute"
              ref={ref}
            >
              {dateRangeText}
            </span>
            <div
              className={joinClassNames(
                "hidden sm:flex items-center absolute bottom-2 right-2 ml-2 p-1 text-sm overflow-clip",
                dateRangeVisible ? "gap-1" : "",
                isShopsite
                  ? "bg-indigo-100 text-indigo-800 rounded-lg"
                  : "bg-[color:var(--accent-3)] text-[color:var(--accent-a11)] backdrop-blur rounded-[var(--radius-2)]"
              )}
            >
              <div className="flex items-center justify-center w-5 h-5 shrink-0">
                <CalendarIcon className="h-4 w-4 shrink-0" />
              </div>
              <motion.span
                initial={{
                  width: 0,
                  height: "20px",
                  opacity: 0,
                }}
                animate={
                  hoveringDate
                    ? {
                        width: "auto",
                        height: clientHeight,
                        opacity: 1,
                        transition: { opacity: { delay: 0.3 } },
                      }
                    : {
                        width: 0,
                        height: "20px",
                        opacity: 0,
                        transition: {
                          width: { delay: 0.2 },
                          height: { delay: 0.2 },
                        },
                      }
                }
                className="pointer-events-none will-change-transform"
              >
                {dateRangeText}
              </motion.span>
            </div>
          </>
        ) : (
          <></>
        )}
      </div>
      <Text
        size="4"
        weight="bold"
        whitelabelClasses="sm:group-hover:text-[color:var(--accent-11)] transition-colors ease-in-out duration-500"
        shopsiteClasses="text-lg font-semibold"
      >
        {landingPage.name}
      </Text>
      {dateRangeText ? (
        <Badge
          size="2"
          sharedClasses="sm:hidden w-fit max-w-full"
          shopsiteClasses="gap-0.5 !px-1 !py-1 !rounded-lg"
        >
          <CalendarIcon className="h-4 w-4 shrink-0" />
          <span className="max-w-[calc(100%-18px)] truncate">
            {dateRangeText}
          </span>
        </Badge>
      ) : (
        <></>
      )}
      {landingPage.region ? (
        <Badge
          color="gray"
          size="2"
          sharedClasses="w-fit"
          shopsiteClasses="gap-0.5 !px-1 !py-1 !rounded-lg max-w-full"
        >
          <MapPinIcon className="h-4 w-4" />
          <span className="max-w-[calc(100%-18px)] truncate">
            {landingPage.region}
          </span>
        </Badge>
      ) : (
        <></>
      )}
    </Link>
  );
}
